import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateRadar } from '../hooks/useLLR'
import { MapPin, Plus, X, ArrowLeft, Loader2, Radar } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export function RadarSetupPage() {
  const navigate = useNavigate()
  const createRadar = useCreateRadar()

  const [form, setForm] = useState({
    name: '',
    geo_lat: '',
    geo_lng: '',
    geo_radius_km: 5,
    geo_label: '',
    geo_code: '',
    place_category: '',
    peak_threshold: 60,
    sync_frequency_h: 2,
    notify_email: true,
    notify_inapp: true,
  })
  const [keywords, setKeywords] = useState<string[]>([])
  const [kwInput, setKwInput] = useState('')

  const addKeyword = () => {
    const kw = kwInput.trim()
    if (kw && !keywords.includes(kw) && keywords.length < 20) {
      setKeywords([...keywords, kw])
      setKwInput('')
    }
  }

  const removeKeyword = (kw: string) => {
    setKeywords(keywords.filter((k) => k !== kw))
  }

  const handleSubmit = async () => {
    if (!form.name || !form.geo_lat || !form.geo_lng || keywords.length === 0) return
    try {
      await createRadar.mutateAsync({
        ...form,
        geo_lat: parseFloat(form.geo_lat),
        geo_lng: parseFloat(form.geo_lng),
        keywords,
      })
      navigate('/llr')
    } catch (e) {
      console.error('Create radar failed', e)
    }
  }

  const categories = [
    'plumber', 'electrician', 'dentist', 'restaurant', 'bakery',
    'hairdresser', 'mechanic', 'lawyer', 'accountant', 'real_estate',
  ]

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/llr')}
          className="w-9 h-9 rounded-lg bg-paper-warm flex items-center justify-center hover:bg-paper-warm transition-colors"
        >
          <ArrowLeft size={18} className="text-ink" />
        </button>
        <div>
          <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Nouveau Radar</h1>
          <p className="text-ink-muted text-sm">Configurez une zone de surveillance</p>
        </div>
      </div>

      <div className="bg-white rounded-card border border-ink-border divide-y divide-ink-border/30">
        {/* Name */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-2">Nom du radar</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="ex : Plomberie Paris 11"
            className="w-full px-3 py-2.5 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
          />
        </div>

        {/* Geo */}
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={16} className="text-brick" />
            <span className="text-sm font-semibold text-ink">Zone géographique</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Latitude</label>
              <input
                type="text"
                value={form.geo_lat}
                onChange={(e) => setForm({ ...form, geo_lat: e.target.value })}
                placeholder="48.8566"
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Longitude</label>
              <input
                type="text"
                value={form.geo_lng}
                onChange={(e) => setForm({ ...form, geo_lng: e.target.value })}
                placeholder="2.3522"
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Label de zone</label>
              <input
                type="text"
                value={form.geo_label}
                onChange={(e) => setForm({ ...form, geo_label: e.target.value })}
                placeholder="Paris 11e arrondissement"
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Rayon (km)</label>
              <input
                type="number"
                value={form.geo_radius_km}
                onChange={(e) => setForm({ ...form, geo_radius_km: parseInt(e.target.value) || 5 })}
                min={1} max={50}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Code géo (pour Trends)</label>
            <input
              type="text"
              value={form.geo_code}
              onChange={(e) => setForm({ ...form, geo_code: e.target.value })}
              placeholder="FR-75"
              className="w-full max-w-xs px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
        </div>

        {/* Category */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-2">Catégorie d'activité</label>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setForm({ ...form, place_category: cat })}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border',
                  form.place_category === cat
                    ? 'bg-brick text-white border-brick'
                    : 'bg-white text-ink border-ink-border hover:border-gold'
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Keywords */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-ink mb-2">
            Mots-clés à surveiller <span className="font-normal text-ink-faint">({keywords.length}/20)</span>
          </label>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={kwInput}
              onChange={(e) => setKwInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              placeholder="ex : plombier urgence"
              className="flex-1 px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              onClick={addKeyword}
              disabled={!kwInput.trim()}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-paper-warm text-sm font-medium text-ink hover:bg-paper-warm transition-colors disabled:opacity-40"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {keywords.map((kw) => (
                <span key={kw} className="flex items-center gap-1.5 bg-gold-light text-brick-dark px-2.5 py-1 rounded-full text-sm font-medium">
                  {kw}
                  <button onClick={() => removeKeyword(kw)} className="hover:text-brick-dark">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Thresholds */}
        <div className="p-5 space-y-4">
          <label className="block text-sm font-semibold text-ink">Paramètres de détection</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ink-muted mb-1">
                Seuil d'alerte ({form.peak_threshold}/100)
              </label>
              <input
                type="range"
                min={10} max={100} step={5}
                value={form.peak_threshold}
                onChange={(e) => setForm({ ...form, peak_threshold: parseInt(e.target.value) })}
                className="w-full accent-gold"
              />
              <div className="flex justify-between text-[11px] text-ink-faint mt-1">
                <span>Sensible</span><span>Strict</span>
              </div>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Fréquence sync</label>
              <select
                value={form.sync_frequency_h}
                onChange={(e) => setForm({ ...form, sync_frequency_h: parseInt(e.target.value) })}
                className="w-full px-3 py-2 rounded-lg border border-ink-border text-sm focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value={1}>Toutes les heures</option>
                <option value={2}>Toutes les 2h</option>
                <option value={4}>Toutes les 4h</option>
                <option value={12}>2x par jour</option>
                <option value={24}>Quotidien</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          onClick={() => navigate('/llr')}
          className="px-4 py-2.5 rounded-card text-sm font-medium text-ink hover:bg-paper-warm transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={handleSubmit}
          disabled={createRadar.isPending || !form.name || !form.geo_lat || !form.geo_lng || keywords.length === 0}
          className="flex items-center gap-2 rounded-card bg-brick px-6 py-2.5 text-sm font-semibold text-white hover:bg-brick-hover transition-colors disabled:opacity-50"
        >
          {createRadar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Radar size={16} />}
          Créer le radar
        </button>
      </div>
    </div>
  )
}
